import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, ShoppingCart, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { CartPanel } from "@/components/CartPanel";
import { PaymentDialog } from "@/components/PaymentDialog";
import { ReceiptDialog } from "@/components/ReceiptDialog";
import { api, formatRupiah } from "@/lib/api";
import { parseOrderParams } from "@/lib/order";
import { EMPTY_SEL, hasOptions, makeLine } from "@/lib/options";
import { OptionPicker } from "@/components/OptionPicker";

const EMPTY_CUSTOMER = { name: "", wa: "", delivery: null };

const useIsDesktop = () => {
  const q = "(min-width: 1024px)";
  const [m, setM] = useState(window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const h = () => setM(mq.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return m;
};

const ProductCard = ({ p, inCart, onAdd }) => {
  const out = p.stock - inCart <= 0;
  return (
    <button
      onClick={() => onAdd(p)}
      disabled={out}
      data-testid={`pos-product-card-${p.id}`}
      className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-4 text-left transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{p.category}</span>
      <span className="mt-1 line-clamp-2 min-h-[2.5rem] text-sm font-semibold text-slate-800">{p.name}</span>
      <span className="mt-2 font-mono text-base font-bold text-emerald-700">{formatRupiah(p.price)}</span>
      <span className={`mt-2 w-fit rounded-full px-2 py-0.5 text-[11px] font-semibold ${p.stock === 0 ? "bg-red-100 text-red-700" : p.is_low_stock ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
        {p.stock === 0 ? "Habis" : `Stok ${p.stock}`}
      </span>
    </button>
  );
};

export default function Kasir() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("Semua");
  const [cart, setCart] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const isDesktop = useIsDesktop();
  const [params, setParams] = useSearchParams();
  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const order = parseOrderParams(params);
    if (!order || !products.length) return;
    const items = order.items
      .map(({ id, qty, sel }) => { const p = products.find((x) => x.id === id); return p && p.stock > 0 ? makeLine(p, sel, Math.min(qty, p.stock)) : null; })
      .filter(Boolean);
    setCart(items);
    setCustomer({ name: order.name, wa: order.wa, delivery: order.delivery });
    setParams({}, { replace: true });
    toast.success(`Pesanan ${order.name || "pelanggan"} dimuat ke keranjang`);
    if (items.length < order.items.length) toast.warning(`${order.items.length - items.length} item tidak tersedia / stok habis`);
  }, [params, products, setParams]);

  const categories = useMemo(() => ["Semua", ...new Set(products.map((p) => p.category))], [products]);
  const filtered = products.filter(
    (p) => (cat === "Semua" || p.category === cat) && `${p.name} ${p.sku}`.toLowerCase().includes(search.toLowerCase())
  );
  const total = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const qtyOf = (id) => cart.filter((c) => c.id === id).reduce((s, c) => s + c.qty, 0);
  const [picking, setPicking] = useState(null);

  const addLine = (p, sel, n = 1) => {
    if (qtyOf(p.id) + n > p.stock) return toast.error(`Stok ${p.name} tidak cukup`);
    const line = makeLine(p, sel, n);
    setCart((c) => (c.some((x) => x.key === line.key) ? c.map((x) => (x.key === line.key ? { ...x, qty: x.qty + n } : x)) : [...c, line]));
  };
  const add = (p) => (hasOptions(p) ? setPicking(p) : addLine(p, EMPTY_SEL));
  const changeQty = (key, d) => {
    const line = cart.find((x) => x.key === key);
    const p = products.find((x) => x.id === line.id);
    if (d > 0 && qtyOf(line.id) >= p.stock) return toast.error(`Stok ${p.name} tidak cukup`);
    setCart((c) => c.map((x) => (x.key === key ? { ...x, qty: x.qty + d } : x)).filter((x) => x.qty > 0));
  };
  const remove = (key) => setCart((c) => c.filter((x) => x.key !== key));

  const onSuccess = (tx) => {
    setPayOpen(false);
    setSheetOpen(false);
    setCart([]);
    setCustomer(EMPTY_CUSTOMER);
    setReceipt(tx);
    load();
  };

  const checkout = () => { setSheetOpen(false); setPayOpen(true); };
  const cartProps = { cart, total, onQty: changeQty, onRemove: remove, onClear: () => setCart([]), onCheckout: checkout };

  return (
    <div className="flex gap-6 pb-24 lg:pb-0" data-testid="kasir-page">
      <div className="min-w-0 flex-1 space-y-4">
        {(customer.name || customer.wa) && (
          <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="kasir-customer-banner">
            <UserRound className="h-4 w-4 shrink-0" />
            <span className="flex-1">Pesanan dari <b>{customer.name || "pelanggan"}</b>{customer.wa && ` · WA ${customer.wa}`}{customer.delivery && ` · Diantar${customer.delivery.fee != null ? ` (ongkir ${formatRupiah(customer.delivery.fee)})` : " (ongkir menyusul)"}`}</span>
            <button onClick={() => setCustomer(EMPTY_CUSTOMER)} className="text-xs font-semibold hover:underline" data-testid="kasir-customer-clear">Hapus</button>
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari produk atau kode..." className="h-11 bg-white pl-9" data-testid="pos-search-input" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="pos-category-filter">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`pos-category-${c.toLowerCase()}`}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${cat === c ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100"}`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
          {filtered.map((p) => <ProductCard key={p.id} p={p} inCart={qtyOf(p.id)} onAdd={add} />)}
        </div>
        {filtered.length === 0 && <p className="py-10 text-center text-sm text-slate-500" data-testid="pos-no-products">Produk tidak ditemukan.</p>}
      </div>

      {isDesktop ? (
        <aside className="sticky top-24 h-[calc(100vh-8rem)] w-[360px] shrink-0 rounded-2xl border border-slate-200 bg-white p-5">
          <CartPanel {...cartProps} />
        </aside>
      ) : (
        <>
          <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white p-3">
            <Button onClick={() => setSheetOpen(true)} className="h-12 w-full justify-between bg-slate-900 hover:bg-slate-800" data-testid="mobile-cart-open-button">
              <span className="flex items-center gap-2"><ShoppingCart className="h-4 w-4" /> {cart.reduce((s, c) => s + c.qty, 0)} item</span>
              <span className="font-mono font-bold">{formatRupiah(total)}</span>
            </Button>
          </div>
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetContent side="bottom" className="h-[85vh] rounded-t-2xl">
              <SheetTitle className="sr-only">Keranjang</SheetTitle>
              <CartPanel {...cartProps} />
            </SheetContent>
          </Sheet>
        </>
      )}

      <OptionPicker product={picking} onClose={() => setPicking(null)} onAdd={(sel, n) => { addLine(picking, sel, n); setPicking(null); }} />
      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} cart={cart} total={total} customer={customer} onSuccess={onSuccess} />
      <ReceiptDialog tx={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}
